
import React from 'react';
import { MapPin, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { DetailedOpeningHours } from '@/types/terrace';
import { TypicalSunnyHours } from './TypicalSunnyHours';

interface TerraceInfoSectionProps {
  name: string;
  address: string;
  openingHours?: string;
  detailedOpeningHours?: DetailedOpeningHours;
  terraceId?: string;
}

const TerraceInfoSection = ({ name, address, openingHours, detailedOpeningHours, terraceId }: TerraceInfoSectionProps) => {
  const formatDetailedHours = (hours: DetailedOpeningHours) => {
    const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayLabels = {
      monday: 'Mon',
      tuesday: 'Tue', 
      wednesday: 'Wed',
      thursday: 'Thu',
      friday: 'Fri',
      saturday: 'Sat',
      sunday: 'Sun'
    };

    return daysOrder.map(day => {
      const dayHours = hours[day as keyof DetailedOpeningHours];
      return {
        day: dayLabels[day as keyof typeof dayLabels],
        isOpen: dayHours.isOpen,
        hours: dayHours.isOpen ? `${dayHours.from} - ${dayHours.to}` : 'Closed'
      };
    });
  };

  const shouldShowDetailedHours = detailedOpeningHours && Object.values(detailedOpeningHours).some(day => 
    day.isOpen || day.from || day.to
  );

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-bold mb-2 text-sunset-dark">{name}</h1>
      
      <div className="flex items-center text-sunset-medium mb-4">
        <MapPin className="w-4 h-4 mr-1" />
        <span>{address}</span>
      </div>
      
      {(openingHours || shouldShowDetailedHours) && (
        <Card className="bg-warm-cream/50 border-peach-orange/20">
          <CardContent className="p-4">
            <div className="flex items-center mb-3">
              <Clock className="w-4 h-4 mr-2 text-peach-orange" />
              <h3 className="font-medium text-sunset-dark">Opening Hours</h3>
            </div>
            
            {shouldShowDetailedHours ? (
              <div className="space-y-2">
                {formatDetailedHours(detailedOpeningHours!).map(({ day, isOpen, hours }) => (
                  <div key={day} className="flex justify-between items-center text-sm">
                    <span className="font-medium text-sunset-dark min-w-[40px]">{day}</span>
                    <span className={`${isOpen ? 'text-sunset-medium' : 'text-sunset-medium/60'}`}>
                      {hours}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-sunset-medium">{openingHours}</p>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Typical Sunny Hours section */}
      {terraceId && (
        <div className="mt-4">
          <TypicalSunnyHours terraceId={terraceId} />
        </div>
      )}
    </div>
  );
};

export default TerraceInfoSection;
